// IndexedDB wrapper for offline storage
const DB_NAME = 'nose-tea-pos';
const DB_VERSION = 1;

interface PendingOrder {
  id: string;
  data: any;
  timestamp: number;
  retryCount: number;
}

interface CachedData {
  key: string;
  data: any;
  timestamp: number;
  expiresAt?: number;
}

class OfflineDB {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Store for pending orders
        if (!db.objectStoreNames.contains('pendingOrders')) {
          const ordersStore = db.createObjectStore('pendingOrders', { keyPath: 'id' });
          ordersStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Store for cached menu/category data
        if (!db.objectStoreNames.contains('cache')) {
          const cacheStore = db.createObjectStore('cache', { keyPath: 'key' });
          cacheStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // Store for synced orders (completed)
        if (!db.objectStoreNames.contains('syncedOrders')) {
          db.createObjectStore('syncedOrders', { keyPath: 'id' });
        }
      };
    });
  }

  private getStore(storeName: string, mode: IDBTransactionMode = 'readonly'): IDBObjectStore {
    if (!this.db) throw new Error('Database not initialized');
    const transaction = this.db.transaction(storeName, mode);
    return transaction.objectStore(storeName);
  }

  // Pending Orders Operations
  async addPendingOrder(order: PendingOrder): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('pendingOrders', 'readwrite');
      const request = store.add(order);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingOrders(): Promise<PendingOrder[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('pendingOrders');
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async removePendingOrder(id: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('pendingOrders', 'readwrite');
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async updatePendingOrder(order: PendingOrder): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('pendingOrders', 'readwrite');
      const request = store.put(order);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Cache Operations
  async setCache(key: string, data: any, ttl?: number): Promise<void> {
    const cacheData: CachedData = {
      key,
      data,
      timestamp: Date.now(),
      expiresAt: ttl ? Date.now() + ttl : undefined,
    };

    return new Promise((resolve, reject) => {
      const store = this.getStore('cache', 'readwrite');
      const request = store.put(cacheData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getCache(key: string): Promise<any | null> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('cache');
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result as CachedData | undefined;
        if (!result) {
          resolve(null);
          return;
        }

        // Check if expired
        if (result.expiresAt && result.expiresAt < Date.now()) {
          this.removeCache(key);
          resolve(null);
          return;
        }

        resolve(result.data);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async removeCache(key: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('cache', 'readwrite');
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clearExpiredCache(): Promise<void> {
    const store = this.getStore('cache', 'readwrite');
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const items = request.result as CachedData[];
        const now = Date.now();
        const deletePromises = items
          .filter((item) => item.expiresAt && item.expiresAt < now)
          .map((item) => this.removeCache(item.key));

        Promise.all(deletePromises).then(() => resolve());
      };
      request.onerror = () => reject(request.error);
    });
  }

  // Synced Orders Operations
  async addSyncedOrder(order: any): Promise<void> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('syncedOrders', 'readwrite');
      const request = store.add(order);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getSyncedOrders(limit = 100): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const store = this.getStore('syncedOrders');
      const request = store.getAll();
      request.onsuccess = () => {
        const results = request.result;
        resolve(results.slice(0, limit));
      };
      request.onerror = () => reject(request.error);
    });
  }

  async clearOldSyncedOrders(daysToKeep = 7): Promise<void> {
    const cutoffTime = Date.now() - daysToKeep * 24 * 60 * 60 * 1000;
    const store = this.getStore('syncedOrders', 'readwrite');
    const request = store.getAll();

    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const orders = request.result;
        const deletePromises = orders
          .filter((order) => order.createdAt < cutoffTime)
          .map(
            (order) =>
              new Promise<void>((res, rej) => {
                const delRequest = store.delete(order.id);
                delRequest.onsuccess = () => res();
                delRequest.onerror = () => rej(delRequest.error);
              })
          );

        Promise.all(deletePromises).then(() => resolve());
      };
      request.onerror = () => reject(request.error);
    });
  }
}

// Singleton instance
export const offlineDB = new OfflineDB();
